const palette = {
  accent100: 'oklch(97% 0.06 170)',
  accent200: 'oklch(90% 0.09 170)',
  accent300: 'oklch(82% 0.12 170)',
  accent400: 'oklch(74% 0.15 170)',
  accent500: 'oklch(70% 0.17 170)',
  accent600: 'oklch(60% 0.17 170)',
  accent700: 'oklch(52% 0.16 170)',
  accent800: 'oklch(44% 0.14 170)',
  accent900: 'oklch(36% 0.12 170)',
  neutral000: 'oklch(100% 0 0)',
  neutral050: 'oklch(98% 0.002 250)',
  neutral100: 'oklch(95% 0.003 250)',
  neutral200: 'oklch(88% 0.005 250)',
  neutral300: 'oklch(78% 0.006 250)',
  neutral400: 'oklch(65% 0.007 250)',
  neutral500: 'oklch(55% 0.008 250)',
  neutral600: 'oklch(45% 0.009 250)',
  neutral700: 'oklch(35% 0.009 250)',
  neutral800: 'oklch(25% 0.009 250)',
  neutral900: 'oklch(18% 0.009 250)',
}

export type ThemeMode = 'light' | 'dark' | 'system'

const lightColors = {
  primaryLight: palette.accent200,
  primary: 'oklch(70% 0.17 170)',
  primaryDark: 'oklch(44% 0.14 170)',
  secondaryLight: '#8e96a3',
  secondary: '#6b7280',
  secondaryDark: '#4b5563',
  background: palette.neutral000,
  surface: palette.neutral100,
  surfaceHover: palette.neutral200,
  cardBackground: palette.neutral050,
  text: palette.neutral800,
  textSecondary: palette.neutral600,
  textMuted: palette.neutral500,
  textInverted: palette.neutral100,
  border: palette.neutral200,
  borderLight: palette.neutral100,
  borderHover: palette.neutral300,
  error: '#ef4444',
  errorLight: '#ef444430',
  warning: '#f1cf47',
  success: '#83ef44',
}

const darkColors = {
  primaryLight: palette.accent200,
  primary: 'oklch(70% 0.17 170)',
  primaryDark: 'oklch(44% 0.14 170)',
  secondaryLight: '#9a9898',
  secondary: '#bdb4b4',
  secondaryDark: '#999999',
  background: palette.neutral900,
  surface: palette.neutral700,
  surfaceHover: palette.neutral600,
  cardBackground: palette.neutral800,
  text: palette.neutral200,
  textSecondary: '#b3b3b3',
  textMuted: '#808080',
  textInverted: palette.neutral700,
  border: palette.neutral700,
  borderLight: palette.neutral800,
  borderHover: palette.neutral400,
  error: '#f14747',
  errorLight: '#ef444430',
  warning: '#bfa439',
  success: '#379e5b',
}

export const getThemeColors = (mode: 'light' | 'dark') => {
  return mode === 'light' ? lightColors : darkColors
}

export const baseTheme = {
  spacing: {
    xs: '4px',
    sm: '8px',
    md: '16px',
    lg: '24px',
    xl: '32px',
    xxl: '48px',
  },
  borderRadius: {
    sm: '4px',
    md: '8px',
    lg: '12px',
    xl: '16px',
  },
  shadows: {
    light: {
      sm: '0 2px 4px rgba(0, 0, 0, 0.1)',
      md: '0 4px 8px rgba(0, 0, 0, 0.15)',
      lg: '0 8px 16px rgba(0, 0, 0, 0.2)',
    },
    dark: {
      sm: '0 2px 4px rgba(0, 0, 0, 0.3)',
      md: '0 4px 8px rgba(0, 0, 0, 0.4)',
      lg: '0 8px 16px rgba(0, 0, 0, 0.5)',
    },
  },
  typography: {
    xs: '12px',
    sm: '14px',
    md: '16px',
    lg: '18px',
    xl: '24px',
    xxl: '32px',
  },
  transitions: {
    default: 'all 0.2s ease-in-out',
    slow: 'all 0.3s ease-in-out',
  },
}

export const createTheme = (mode: 'light' | 'dark') => ({
  ...baseTheme,
  colors: getThemeColors(mode),
  shadows: baseTheme.shadows[mode],
})

// Default theme (dark mode for backward compatibility)
export const theme = createTheme('dark')
